import { useSyncExternalStore } from 'react'

/**
 * Whether another modal dialog is on the page - `FRONTEND.md` §9.737, corrected after review.
 *
 * **Why the push offer has to ask.** It opens on its own schedule - once the browser's probe and the server's list
 * have answered, which on a slow read is seconds after the page drew - not on a press. At its own `z-[80]` it then
 * landed underneath whatever was already open above it: the lunch menu (`z-[90]`), the wheel (`z-[95]`) or the
 * branch picker's opaque welcome screen (`z-[100]`). And `useModalFocus` moved the keyboard's focus to its close
 * button anyway, behind the dialog the customer was looking at - so when that one closed, its focus went back to the
 * page behind the offer's backdrop. So while any other dialog that says `aria-modal="true"` is on the page, the
 * offer is not shown, and it is shown - topmost, with the focus - the moment the last one closes.
 *
 * **Whichever opened first.** Two dialogs open on their own schedule below the offer's `z-[80]` - the gift offer
 * (`z-[60]`), from a stored basket and the rules read, and the add-on prompt (`z-[70]`), after the read a press
 * started - so a dialog opening after the offer can be drawn underneath it, with the keyboard's focus moved there.
 * The offer steps aside for it too: hidden, and kept mounted so `useModalFocus` does not hand the focus it took back
 * to the page from under the newer dialog; it comes back, and takes the focus back, when that one closes. Waiting
 * for a dialog that was up before it, the offer is not mounted at all, so it takes no focus it could not show. Which
 * of the two it is, is what {@link offerIsOnPage} tells the dialog.
 *
 * **Every dialog that says so is counted, not a list of names**, because the customer app's dialogs live in modules
 * this one may not import (§5), and `EveryModalOverlayConfinesFocusTest` already holds every one of them to a
 * truthful `aria-modal`. Three overlays carry no dialog role at all - the pizza builder, its extras prompt and the
 * cookie card's customise panel - and are not seen here; the offer is drawn over them, above their `z-[60]` and
 * `z-[70]`, and nothing is hidden.
 *
 * In a `.ts` file rather than beside the dialog: the census counts the attribute in `.tsx` files, one per overlay,
 * and a selector that names it is not an overlay.
 */
const MODAL = '[aria-modal="true"]'
/** The offer's own panel, which carries `data-push-prompt` - it says `aria-modal` too, and is not another dialog. */
const THE_OFFER = '[data-push-prompt]'

/** Whether a modal dialog other than the push offer is on the page right now. */
export function anotherDialogIsOpen(): boolean {
  if (typeof document === 'undefined') return false
  return [...document.querySelectorAll(MODAL)].some((dialog) => !dialog.matches(THE_OFFER))
}

/** Whether the offer itself is on the page - shown, or stepped aside and hidden. */
export function offerIsOnPage(): boolean {
  return typeof document !== 'undefined' && document.querySelector(THE_OFFER) !== null
}

/** What the page holds, as one value: 1 for another dialog, 2 for the offer itself. A number, so it compares by value. */
function pageNow(): number {
  return (anotherDialogIsOpen() ? 1 : 0) | (offerIsOnPage() ? 2 : 0)
}

/** Wakes the reader whenever a dialog is added, removed, or changes what it says about itself. */
function watchDialogs(onChange: () => void): () => void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => undefined
  const observer = new MutationObserver(onChange)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-modal'],
  })
  return () => observer.disconnect()
}

function watchNothing(): () => void {
  return () => undefined
}

function nothingOnPage(): number {
  return 0
}

/** What the page holds besides everything the offer's rule already knows. */
export interface PageDialogs {
  /** A modal dialog other than the offer is on the page. */
  anotherDialogOpen: boolean
  /** The offer is on the page - so if another dialog has come, it came after the offer was shown. */
  offerOnPage: boolean
}

/**
 * {@link anotherDialogIsOpen} and {@link offerIsOnPage}, as values a component re-renders on.
 *
 * `watching` is whether the offer is wanted at all: only then is the page watched, so a visitor who is not signed in,
 * or a customer who has answered, costs nothing.
 */
export function usePageDialogs(watching: boolean): PageDialogs {
  const page = useSyncExternalStore(
    watching ? watchDialogs : watchNothing,
    watching ? pageNow : nothingOnPage,
    nothingOnPage,
  )
  return { anotherDialogOpen: (page & 1) !== 0, offerOnPage: (page & 2) !== 0 }
}
